import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/redux";

import MTopBar from "@/components/ui/atoms/MTopBar";
import MVideoPlayer from "@/components/ui/atoms/MVideoPlayer";

import styles from "./ExploreScreen.module.css";

const RINGS = [
  { size: 540, top: -5, left: 16 },
  { size: 540, top: 45, left: 51 },
  { size: 540, top: 80, left: 80 },
  { size: 540, top: 120, left: 120 },
  { size: 460, top: 160, left: 160 },
];

export interface ExploreCard {
  img: string;
  title: string;
  thumbGradient: string;
  videoUrl: string;
  imgStyle: React.CSSProperties;
}

interface ExploreScreenProps {
  pill: string;
  bodyText: string;
  cards: [ExploreCard, ExploreCard, ExploreCard];
}

const ExploreScreen: React.FC<ExploreScreenProps> = ({
  pill,
  bodyText,
  cards,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  return (
    <div className={styles.page}>
      <div className={styles.ellipses} aria-hidden>
        {RINGS.map(({ size, top, left }, i) => (
          <svg
            key={i}
            style={{ position: "absolute", top, left, display: "block" }}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            fill="none"
            overflow="visible"
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 - 1.35}
              stroke="#8764BB"
              strokeWidth="2.7"
            />
          </svg>
        ))}
      </div>

      <MTopBar
        onBack={() => navigate(-1)}
        exitPath="/dashboard"
        hideProfilePicker
        disableSticky
        skipExitConfirmation
        style={{ position: "relative", zIndex: 1 }}
      />

      <div className={styles.content}>
        <div className={styles.introSection}>
          <div className={styles.pill}>{pill}</div>
          <p className={styles.bodyText}>{bodyText}</p>
        </div>

        <div className={styles.cardsSection}>
          <h2 className={styles.heading}>{t("auth.exploreScreen.heading")}</h2>
          <div className={styles.cardList}>
            {cards.map((card, i) => (
              <button
                key={i}
                type="button"
                className={styles.card}
                onClick={() => setActiveVideo(card.videoUrl)}
              >
                <div
                  className={styles.thumbnail}
                  style={{ background: card.thumbGradient }}
                >
                  <img
                    src={card.img}
                    alt=""
                    className={styles.thumbImg}
                    style={card.imgStyle}
                  />
                </div>
                <div className={styles.cardBody}>
                  <p className={styles.cardTitle}>{card.title}</p>
                  <div className={styles.showMeRow}>
                    <span className={styles.showMeText}>
                      {t("auth.exploreScreen.showMe")}
                    </span>
                    <span className={styles.playBadge}>
                      <svg
                        width="9.6"
                        height="9.6"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <polygon points="2,1 9,5 2,9" fill="#7046ae" />
                      </svg>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {!isAuthenticated && (
        <div className={styles.cta}>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={() => navigate("/register/info")}
          >
            <span className={styles.ctaPrimary}>
              {t("auth.exploreScreen.ctaPrimary")}
            </span>
            <span className={styles.ctaSecondary}>
              {t("auth.exploreScreen.ctaSecondary")}
            </span>
          </button>
        </div>
      )}

      {activeVideo && (
        <MVideoPlayer
          videoUrl={activeVideo}
          open={true}
          onClose={() => setActiveVideo(null)}
          autoplay
        />
      )}
    </div>
  );
};

export default ExploreScreen;
